import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {AchooInputComponent} from '../../../core/achoo-input/achoo-input.component';
import {InfluencerSuggestion} from '../../../../models/influencer-suggestion';
import {GroupService} from '../../../services/group-details/group.service';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, tap} from 'rxjs/operators';
import {Observable} from 'rxjs/Observable';
import {CommonConstant, listOrderedPlatforms} from '../../../app.constants';
import {ValidationHandlerService} from '../../../core/validation-handler.service';
import {NgSelectComponent} from '@ng-select/ng-select';
import {isArray} from 'rxjs/util/isArray';

@Component({
  selector: 'auto-suggestion-influencer',
  templateUrl: './auto-suggestion-influencer.component.html',
  styleUrls: ['./auto-suggestion-influencer.component.scss']
})
export class AutoSuggestionInfluencerComponent extends AchooInputComponent<any> {

  @ViewChild('ngSelectAutoSuggestionBox')
  ngSelectComp: NgSelectComponent;
  @Input()
  public numberSelectedInfluencer = 0;
  @Output()
  public selectInfluencerEvent = new EventEmitter<any>();
  @Output()
  public enterEvent = new EventEmitter<any>();
  @Output()
  public afterLoadingSuggestion = new EventEmitter<any>();
  @Output()
  public addNewInfluencer = new EventEmitter<any>();
  public suggestionInfluencers: InfluencerSuggestion[];
  public suggestionLoading = false;
  public hasErrorWhenSearching = false;

  public typeahead = new EventEmitter<string>();
  public selectedInfluencer: any;
  public platformList: any[] = [];

  private searchQuery: string | null;
  private scrollId: string;
  private totalResults: number;
  private numberOfItemsFromEndBeforeFetchingMore = 10;

  constructor(private groupService: GroupService,
              private cd: ChangeDetectorRef,
              protected validationHandlerService: ValidationHandlerService,
              private renderer: Renderer2,
              private elementRef: ElementRef) {
    super(validationHandlerService);
    this.platformList = listOrderedPlatforms;
    renderer.listen(elementRef.nativeElement, 'keyup', (evt) => {
      if (evt.code === CommonConstant.ENTER_CODE && !this.suggestionLoading
        && Array.isArray(this.suggestionInfluencers) && this.suggestionInfluencers.length > 0) {
        // waiting for 200ms before emitting. Because time debounce of auto complete is set 200ms
        setTimeout(() => {
          this.enterEvent.emit({
            handle: this.searchQuery,
            isLoading: this.suggestionLoading,
            suggestionList: this.suggestionInfluencers
          });
        }, 200);
      }
    });

    this.typeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      tap(() => {
        this.hasErrorWhenSearching = false;
        this.suggestionLoading = true;
        this.suggestionInfluencers = [];
        this.scrollId = undefined;
      }),
      switchMap(term => this.loadSuggestion(term))
    ).subscribe((res) => {
      this.suggestionInfluencers = res;
      this.afterLoadingSuggestion.emit({
        suggestionList: res,
        hasError: this.hasErrorWhenSearching
      });
    });
  }

  loadSuggestion(term: string, matchExtractly?: boolean) {
    this.searchQuery = term;
    return this.groupService.loadInfluencerSuggestion(term, this.scrollId).pipe(
      catchError((err: Response) => {
        // WS will throw 404 error when there is no influencer match with term
        // So, ignore this error
        if (err.status !== 404) {
          this.hasErrorWhenSearching = true;
        }
        return Observable.of([]);
      }),
      map(items => {
        this.scrollId = items.scrollId;
        this.totalResults = items.totalResults;
        if (items && items.influencers) {
          if (matchExtractly) {
            return items.influencers.filter(item => item.channels.filter(c => c.handle === term).length > 0);
          }
          return items.influencers;
        } else {
          return [];
        }
      }),
      tap(() => this.suggestionLoading = false)
    );
  }

  onScroll({ end }) {
    if (this.suggestionLoading || this.totalResults === this.suggestionInfluencers.length) {
      return;
    }

    if (end + this.numberOfItemsFromEndBeforeFetchingMore >= this.suggestionInfluencers.length) {
      this.fetchMore();
    }
  }

  onScrollEnd() {
    this.fetchMore();
  }

  private fetchMore() {
    this.suggestionLoading = true;
    this.loadSuggestion(this.searchQuery).subscribe((res) => {
      this.suggestionLoading = false;
      this.suggestionInfluencers = this.suggestionInfluencers.concat(res);
    });
  }

  onSelectSuggestion(e) {
    this.selectInfluencerEvent.emit(e);
    this.suggestionInfluencers = [];
  }

  getCountryCode(influencer) {
    return influencer && influencer.country && influencer.country.code ? influencer.country.code.toLowerCase() : CommonConstant.INTERNATION_COUNTRY_CODE;
  }

  hasPlatform(platformId, influencer) {
    if (!influencer || !influencer.channels) {
      return false;
    }

    return influencer.channels.findIndex((channel) => channel.platform && channel.platform.id === platformId) >= 0;
  }

  openSuggestionDropdown(handle, matchExtractly = true) {
    // filter suggestion by handle
    const term = handle.replace('@', '');
    this.ngSelectComp.open();
    this.suggestionInfluencers = [];
    this.suggestionLoading = true;
    this.loadSuggestion(term, matchExtractly).subscribe((res) => {
      this.suggestionInfluencers = res;
    });
  }

  addNewInfluencerManually() {
    this.addNewInfluencer.emit({
      handle: this.searchQuery,
      index: -1,
      influencer: null,
    });
  }

  get canShowAddNewInfluencerLine() {
    return this.suggestionInfluencers && this.suggestionInfluencers.length > 0;
  }
}



// WEBPACK FOOTER //
// ./src/app/main/group-details/auto-suggestion-influencer/auto-suggestion-influencer.component.ts